<template>
  <div :class="wrapperClasses">
    <FormLabel
      v-if="$slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="tw-mb-2"
    >
      <slot name="label" />
    </FormLabel>
    <div class="tw-flex tw-items-center">
      <input
        ref="file-input"
        v-bind="$attrs"
        type="file"
        :class="errorClasses"
        :aria-invalid="isError"
        :aria-describedby="id"
        :aria-readonly="Object.keys($attrs).includes('readonly').toString()"
        :aria-disabled="Object.keys($attrs).includes('disabled').toString()"
        :aria-required="isRequired && isRequired.toString()"
        :id="id"
        @change="handleUpload"
        accept="image/jpeg, image/png"
        tabindex="-1"
        class="tw-hidden"
      />
      <img
        v-if="imagePreview"
        :src="imagePreview"
        alt="Uploaded image preview"
        class="tw-mr-4 tw-h-auto tw-w-24 tw-rounded-md"
      />
      <BaseButton
        @click="$refs['file-input'].click()"
        color="outline-secondary"
      >
        <BaseIcon name="data-upload" class="tw-mr-2 tw-size-4" />
        <slot name="upload-button-label">Choose file</slot>
      </BaseButton>
      <IconButton
        v-if="imagePreview"
        color="outline-red"
        @click="deleteFile"
        class="tw-ml-2"
      >
        <BinIcon />
      </IconButton>
    </div>
    <div v-if="hasErrors || $slots.caption" class="tw-mt-2">
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption v-if="$slots.caption" :class="{ 'tw-mt-1': hasErrors }">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup>
import FormLabel from './FormLabel.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import BaseButton from '../BaseButton.vue'
import IconButton from '../IconButton.vue'
import BinIcon from '@/assets/images/icons/bin.svg'
import { computed, ref } from 'vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'

const emit = defineEmits(['errors', 'uploaded'])

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: [Array, String],
    default: '',
  },
  wrapperClasses: {
    type: [Array, String, Object],
    default: () => [],
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: '',
  },
  preview: {
    type: [String, null],
    default: null,
  },
  // MB
  maxFileSizeInMb: {
    type: Number,
    default: 2,
  },
})

const id = `growthmentor-image-upload-${getUniqueId()}`
const imagePreview = ref(props.preview)

const errorClasses = computed(() => {
  return (
    props.isError &&
    'tw-bg-white tw-border-error-400 tw-text-error-500 placeholder:tw-text-error-500 focus:tw-ring-0 focus:tw-border-error-400'
  )
})
const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})

const handleUpload = (event) => {
  const file = event.target.files[0]
  const reader = new FileReader()
  const size = (file.size / 1024 / 1024).toFixed(4)

  reader.onload = (readerEvent) => {
    if (size > props.maxFileSizeInMb) {
      emit('errors', `Image is too big (max ${props.maxFileSizeInMb}MB)`)
      return
    }
    imagePreview.value = readerEvent.target.result
    emit('uploaded', file)
  }
  reader.readAsDataURL(file)

  // This is actually a workaround to make sure file uploads are working with custom solution
  // event.target.value = null
}
const deleteFile = () => {
  imagePreview.value = null
  if (props.preview) {
    // If we have a preview it means that object already has logo, so if we delete it, we want to completely delete it from backend.
    // null -> delete from backend
    emit('uploaded', null)
  } else {
    // If we don't have a preview it means that there is nothing on backend, so if we delete it, we don't send it to backend.
    // In form object, we have to detect empty string to not send it to backend, example: mentors/show/experience/ExperienceModal
    emit('uploaded', '')
  }
}
</script>

<script>
export default {
  name: 'FormImageUpload',
  inheritAttrs: false,
}
</script>
